import { animate, ImageView } from '@play-co/timestep-core/ui';
import DecorationView, {
  DecorationEditOpts,
  DecorationSlotOpts,
} from './DecorationView';
import uiConfig from 'src/lib/ui/config';
import { DecorationRoomID } from 'src/replicant/decoration/decoration.ruleset';
import { getFriendsPet, getPet } from '../pet';
import { EntityManager } from '../logic/EntityManager';

// Available slots ids
type SlotsID = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// Room ID
const ROOM: DecorationRoomID = 'living';

// Base config for each slot image
const controlConfig = new Map<SlotsID, DecorationSlotOpts>([
  // Wallpaper
  [1, { width: 719, height: 1276, x: -1, y: 4, zIndex: 0 }],
  // Floor
  [2, { width: 740, height: 390, x: -11, y: 665, zIndex: 3 }],
  // Above fireplace
  [3, { width: 225, height: 264, x: 258, y: 167, zIndex: 6 }],
  // Painting
  [4, { width: 217, height: 277, x: 495, y: 226, zIndex: 5 }],
  // Fireplace
  [5, { width: 614, height: 398, x: 51, y: 363, zIndex: 4 }],
  // Tree
  [6, { width: 288, height: 609, x: -3, y: 240, zIndex: 9 }],
  // Couch
  [7, { width: 386, height: 396, x: 325, y: 444, zIndex: 8 }],
  // Side table
  [8, { width: 202, height: 237, x: 150, y: 575, zIndex: 7 }],
]);

/**
 * This view keep all view setup and defaults for this type room
 */
export default class LivingRoomDecorationView extends DecorationView {
  public footerView: ImageView;
  private state: 'default' | 'up' = 'default';

  // Config for slots placeholders
  private config: Map<SlotsID, DecorationSlotOpts>;

  // Config for slots edit buttons placeholders
  private edit: Map<SlotsID, DecorationEditOpts> = new Map([
    // Wallpaper
    [1, { x: 112, y: 209 }],
    // Floor
    [2, { x: 268, y: 895 }],
    // Above fireplace
    [3, { x: 303, y: 382 }],
    // Painting
    [4, { x: 528, y: 394 }],
    // Fireplace
    [5, { x: 309, y: 579 }],
    // Tree
    [6, { x: 47, y: 545 }],
    // Couch
    [7, { x: 503, y: 752 }],
    // Side table
    [8, { x: 183, y: 718 }],
  ]);

  // Default slots content
  private slots: Map<SlotsID, string> = new Map([
    [1, '01_living_room/default/01_wallpaper.png'],
    [2, '01_living_room/default/02_floor.png'],
    [3, '01_living_room/default/03_above_fireplace.png'],
    [4, '01_living_room/default/04_painting.png'],
    [5, '01_living_room/default/05_fireplace.png'],
    [6, '01_living_room/default/06_tree.png'],
    [7, '01_living_room/default/07_couch.png'],
    [8, '01_living_room/default/08_sidetable.png'],
  ]);

  // Header element
  private header = {
    image: 'assets/ui/decoration/frames/living_room_header.png',
    x: -6,
    width: 729,
    height: 223,
    zIndex: 10,
  };

  // Footer scene
  private footer = {
    image: 'assets/ui/decoration/background/living_room_base.png',
    width: 751,
    height: 626,
    x: -18,
    y: 654,
    zIndex: 2,
  };

  /**
   * Build room view and setup abstraction layer
   */
  constructor(opts: { autoDecorationUpdate: boolean }) {
    super(ROOM, {
      width: uiConfig.width,
      height: uiConfig.height,
    });

    this.config = controlConfig;

    // Create slot views
    for (const [slot, defaultImage] of this.slots) {
      this.createSlot(
        slot,
        defaultImage,
        this.config.get(slot)!,
        this.edit.get(slot)!,
      );
    }

    // Add header
    new ImageView({
      tag: 'header',
      superview: this,
      ...this.header,
    });

    // Add footer
    this.footerView = new ImageView({
      tag: 'footer',
      superview: this,
      ...this.footer,
      infinite: true,
    });

    // Connect listeners
    if (opts.autoDecorationUpdate) {
      this.listenSlotChangeEvent();
    }
  }

  public async slideDown() {
    if (this.state === 'default') {
      return;
    }
    this.state = 'default';
    await this.slide('default');
    getPet().conversation.start();
    getFriendsPet()?.conversation.start();
  }

  public async slideUp() {
    if (this.state === 'up') {
      return;
    }
    this.state = 'up';
    await this.pauseConversation();
    await this.slide('up');
  }

  private async pauseConversation() {
    await Promise.all([
      getPet().conversation.waitForFinish(),
      getFriendsPet()?.conversation.waitForFinish() ?? Promise.resolve(),
    ]);

    getPet().conversation.pause();
    getFriendsPet()?.conversation.pause();
  }

  private async slide(state: 'default' | 'up') {
    await animate(this)
      .then(
        (value) => {
          // Invert value if we are going back to default.
          value = state === 'default' ? 1 - value : value;
          const result = value * -320;

          // Move living room.
          Object.values(this.images).forEach((image, index) => {
            if (index === 0) return;
            image.style.offsetY = result;
          });
          this.footerView.style.offsetY = result;

          // Move pets.
          EntityManager.setEntitiesOffset({ offsetX: 0, offsetY: result });
        },
        500,
        animate.easeOut,
      )
      .toPromise();
  }
}
